import React, { useState, useEffect } from 'react';
import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Link as MuiLink, Box } from '@mui/material';
import { titleCase } from "title-case";
import Link from '../components/Link';
import { Link as RouterLink } from 'react-router-dom';
import PlanSelectorDialog from '../components/PlanSelectorDialog';
import DeleteProxyApplicationDialog from '../components/DeleteProxyApplicationDialog';
import { StatusChip, VercelChip } from '../components/ProxyApplicationChips';
import ProxyListEmptyState from '../components/ProxyListEmptyState';
import { Plan, ProxyApplicationListItem } from '../models';
import DashboardPage from '../layouts/DashboardPage';
import ErrorState from '../components/ErrorState';

export interface ProxyApplicationListState {
  apps: ProxyApplicationListItem[];
  lastFetched: Date;
  isLoading: boolean;
  hasLoaded: boolean;
  errorString: string;
  refetchProxyApplications: () => Promise<void>;
}

interface ProxyApplicationListPageProps extends ProxyApplicationListState {
  plans: Plan[];
}

interface ProxyListTableProps {
  apps: ProxyApplicationListItem[],
  openPlanModal: (applicationId: string, plan: Plan) => void,
  openDeleteProxyApplicationModal: (applicationId: string) => void
}

const ProxyListTable = (props: ProxyListTableProps) => (
  <TableContainer>
    <Table sx={{ minWidth: 600 }}>
      <TableHead>
        <TableRow>
          <TableCell>Proxy Application</TableCell>
          <TableCell>Region</TableCell>
          <TableCell>Plan</TableCell>
          <TableCell></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {props.apps.map((row) => (
          <TableRow
            key={row._id}
            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
          >
            <TableCell component="th" scope="row">
              {row.name}
              <Box sx={{ mt: 0.25 }}>
                <StatusChip status={row.status} />
                {row.vercelProjectExternalId &&
                  <VercelChip project={row.vercelProjectExternalId} />
                }
              </Box>
            </TableCell>
            <TableCell>{row.region.name}</TableCell>
            <TableCell>{titleCase(row.plan.name)}<br />{row.plan.httpProxy ? 'HTTP/HTTPS' : 'SOCKS'}</TableCell>
            <TableCell>
              <MuiLink component="button" style={{ textDecoration: "none" }} onClick={() => { props.openPlanModal(row._id, row.plan) }}>Modify Plan</MuiLink><br />
              <Link to={`/apps/${row._id}`} style={{ textDecoration: "none" }}>Details</Link><br />
              <Link to={`/apps/${row._id}/logs`} style={{ textDecoration: "none" }}>Logs</Link><br />
              <MuiLink component="button" style={{ textDecoration: "none" }} onClick={() => { props.openDeleteProxyApplicationModal(row._id) }}>Delete</MuiLink><br />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer >
);

export default (props: ProxyApplicationListPageProps) => {
  const { apps, isLoading, hasLoaded, errorString, refetchProxyApplications, lastFetched } = props;
  const [planSelectorDialogOpen, setPlanSelectorDialogOpen] = useState(false);
  const [deleteProxyApplicationDialogOpen, setDeleteProxyApplicationDialogOpen] = useState(false);
  const [focusedApplicationId, setFocusedApplicationId] = useState('');
  const [focusedApplicationPlan, setFocusedApplicationPlan] = useState(null);

  useEffect(() => {
    // Refetch in background on mount if last fetched more than 2 seconds ago
    if (lastFetched < new Date(new Date().getTime() - 1000 * 2)) {
      refetchProxyApplications();
    }
  }, [lastFetched, refetchProxyApplications]);

  const openPlanModal = (applicationId: string, plan: any) => {
    setFocusedApplicationId(applicationId);
    setFocusedApplicationPlan(plan);
    setPlanSelectorDialogOpen(true);
  }
  const closeModals = () => {
    setPlanSelectorDialogOpen(false);
    setDeleteProxyApplicationDialogOpen(false);
  }

  const openDeleteProxyApplicationModal = (planId: string) => {
    setFocusedApplicationId(planId);
    setDeleteProxyApplicationDialogOpen(true);
  }

  const getBody = () => {
    if (errorString) {
      return <ErrorState errorMessage={errorString} />;
    } else if (apps.length === 0) {
      return <ProxyListEmptyState />;
    } else {
      return <ProxyListTable apps={apps} openPlanModal={openPlanModal} openDeleteProxyApplicationModal={openDeleteProxyApplicationModal} />
    }
  }

  return (
    <DashboardPage
      title="Proxy Applications"
      headerChildren={<Button component={RouterLink} to="/new-app" variant="contained">+ New Proxy Application</Button>}
      isLoading={isLoading && !hasLoaded}
    >
      {getBody()}
      {focusedApplicationPlan && (
        <PlanSelectorDialog plans={props.plans} open={planSelectorDialogOpen} applicationId={focusedApplicationId} applicationPlan={focusedApplicationPlan} onClose={closeModals} onSuccess={() => {
          closeModals();
          refetchProxyApplications();
        }} />
      )}
      <DeleteProxyApplicationDialog open={deleteProxyApplicationDialogOpen} applicationId={focusedApplicationId} onClose={closeModals} onSuccess={() => {
        closeModals();
        refetchProxyApplications();
      }} />
    </DashboardPage >
  );
};
